/**
 * Zod schema for "Add GitHub Connection" (Charter M3 migration), imported by
 * `GitHubConnectionForm.tsx`, never redefined (form-standard.context.md §3). Rule IDs `VGH-*`
 * mirror the pre-migration hand-rolled `validate()` in the original component exactly — same
 * fields, same required/optional split, same messages. No length/format constraints existed
 * pre-migration beyond "required", so none are added here.
 */
import { z } from 'zod';
import type { GitHubConnection, CreateGitHubConnectionRequest } from '../types';
import { GITHUB_INSTALLATION_TYPE_OPTIONS } from './connectionFormFields';

// reason: GITHUB_INSTALLATION_TYPE_OPTIONS is a runtime-verified non-empty array of the exported
// union type; zod's z.enum() requires a literal non-empty tuple type.
const INSTALLATION_TYPE_TUPLE = GITHUB_INSTALLATION_TYPE_OPTIONS.map((o) => o.value) as [
  GitHubConnection['installationType'],
  ...GitHubConnection['installationType'][],
];

export const GitHubConnectionSchema = z.object({
  // VGH-01
  name: z.string().trim().min(1, 'Connection name is required'),
  installationType: z.enum(INSTALLATION_TYPE_TUPLE),
  // VGH-02
  organizationOrUser: z.string().trim().min(1, 'Organization or username is required'),
  // VGH-03
  accessToken: z.string().trim().min(1, 'Access token is required'),
});

/** The shape RHF's `useForm<T>`/`defaultValues` are typed against (pre-transform). */
export type GitHubConnectionFormValues = z.input<typeof GitHubConnectionSchema>;
/** The shape `handleSubmit(onValid)` receives (post-transform — trimmed). */
export type GitHubConnectionFormOutput = z.output<typeof GitHubConnectionSchema>;

// Compile-time proof the schema's output assembles a valid request DTO.
type _AssertAssignable = GitHubConnectionFormOutput extends CreateGitHubConnectionRequest ? true : false;
const _assertAssignable: _AssertAssignable = true;
void _assertAssignable;
